import React, { useState, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../../config/axios";
import {
  Download,
  XCircle,
  FileText,
  Archive,
  Loader,
  CheckCircle,
  AlertCircle,
  X,
  Mail,
  FileSpreadsheet,
  Zap,
  Clock,
} from "lucide-react";
import "./LetterDownload.css"
import { formatDateDD_MM_YYYY, formatDateDDMMYYYY, formattedDateLong } from "../../../components/Date";

const SUB_COMPANY_ID = "a2b6154c-5c4a-4bcb-a4ec-b151c5c652b5";

const DownloadAllLetters = ({
  project,
  letter_status,
  fromDate,
  toDate,
  letterFrom,
  letterTo,
  letter_num,
  workperformedby,
  letter_type,
  letterExchange,
  authority,
  created_from,
  created_to,
  contractor,
  coordinator,
  subCompany
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // "idle", "downloading", "success", "error"
  const [stats, setStats] = useState({ total: 0, fetched: 0 });
  const cancelRef = useRef(false);

  const pageSize = 500;
  const fileLimit = 20000; // 20K per Excel
  const parallelLimit = 10; // 10 parallel calls

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    if (!loading) {
      setIsModalOpen(false);
      setStatus("idle");
      setProgress(0);
    }
  };

  const fetchPage = (page) => {
    return axios.get(
      `${BASE_URL}/siteproject/letterrecordtable/page${page}/${pageSize}/${letter_status}/${fromDate}/${toDate}/${letter_num}/${project}/${workperformedby}/${letter_type}/${letterFrom}/${letterTo}/${contractor}/null/${authority}/${coordinator}/${letterExchange}/${created_from}/${created_to}/${subCompany}/null/null/`
    );
  };

  const createExcelBlob = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Letters");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    return new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      setStatus("downloading");
      setProgress(0);
      setStats({ total: 0, fetched: 0 });
      cancelRef.current = false;

      const zip = new JSZip();

      // Get total count
      const firstRes = await axios.get(
        `${BASE_URL}/siteproject/letterrecordtable/page1/1/${letter_status}/${fromDate}/${toDate}/${letter_num}/${project}/${workperformedby}/${letter_type}/${letterFrom}/${letterTo}/${contractor}/null/${authority}/${coordinator}/${letterExchange}/${created_from}/${created_to}/${subCompany}/null/null/`
      );

      const totalCount = firstRes.data.count;
      setStats((prev) => ({ ...prev, total: totalCount }));

      const totalPages = Math.ceil(totalCount / pageSize);

      let page = 1;
      let buffer = [];
      let fileIndex = 1;
      let totalFetched = 0;

      while (page <= totalPages && !cancelRef.current) {
        const requests = [];

        for (let i = 0; i < parallelLimit && page <= totalPages; i++) {
          requests.push(fetchPage(page));
          page++;
        }

        const responses = await Promise.allSettled(requests);

        for (const res of responses) {
          if (res.status === "fulfilled") {
            const formatted = res.value.data.results.map((item, index) => ({
              Letter_No: item.letter_num,
              Project_Code: item.project_pr_code,
              Project_Short_Name:item.project_short_name,
              Letter_Date: formatDateDD_MM_YYYY(item.letter_date),
              Letter_Add_Date: formatDateDD_MM_YYYY(item.created_at),
              is_timebound: item.is_timebound ? "Yes" : "No",
              tat_duration: item.tat_duration,
              DeadLine: formatDateDD_MM_YYYY(item.end_date),


              authority_engineer_name: item.authority_engineer_name,

              Coordinator: item.com_operators_name?.length > 0
                ? item.com_operators_name.join(", ")
                : "-",
              Subject: item.subject,
              letterExchange: item.letterExchange,
              letter_from: item.letter_from,
              letter_to: item.letter_to,
              status: item.status,
              remarks: item.remarks,
              lettertype: item.lettertype,
              workperformedby: item.workperformedby,
              contractorname: item.contractorname,
              other_type_name: item.other_type_name,
              other_letter_from: item.other_letter_from,
              other_letter_to: item.other_letter_to,
            }));

            buffer.push(...formatted);
            totalFetched += formatted.length;
            setStats((prev) => ({ ...prev, fetched: totalFetched }));
          }
        }

        // Update progress
        const percent = ((totalFetched / totalCount) * 100).toFixed(2);
        setProgress(Number(percent));

        // Create Excel file when 20K reached
        while (buffer.length >= fileLimit) {
          const fileData = buffer.slice(0, fileLimit);
          const blob = createExcelBlob(fileData);

          zip.file(`Letters_${fileIndex}.xlsx`, blob);
          buffer = buffer.slice(fileLimit);
          fileIndex++;
        }
      }

      // Remaining data
      if (buffer.length > 0 && !cancelRef.current) {
        const blob = createExcelBlob(buffer);
        zip.file(`Letters_${fileIndex}.xlsx`, blob);
      }

      if (!cancelRef.current) {
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, "All_Letters.zip");
        setStatus("success");
      } else {
        setStatus("idle");
      }
    } catch (error) {
      console.error("Download Error:", error);
      setStatus("error");
    } finally {
      setLoading(false);
      if (status !== "success") setProgress(0);
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setLoading(false);
    setStatus("idle");
    setProgress(0);
  };

  const handleReset = () => {
    setStatus("idle");
    setProgress(0);
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      closeModal();
    }
  };

  // Animation variants
  const getIcon = () => {
    switch (status) {
      case "downloading":
        return <Loader className="status-icon downloading" />;
      case "success":
        return <CheckCircle className="status-icon success" />;
      case "error":
        return <AlertCircle className="status-icon error" />;
      default:
        return <Archive className="status-icon idle" />;
    }
  };

  const getStatusText = () => {
    if (status === "downloading")
      return `Fetching ${stats.fetched.toLocaleString()} of ${stats.total.toLocaleString()} letters`;
    if (status === "success") return "Download complete!";
    if (status === "error") return "Something went wrong";
    return "Ready to export all letters";
  };

  return (
    <>
      {/* Trigger Button */}
      <button onClick={openModal}  className="clearbutton">
                           <span className="transition"></span>

        <span className="trigger-btn-content">
          <Mail className="trigger-btn-icon" />
          Download Letters
        </span>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="download-modal-overlay" onClick={handleBackdropClick}>
          {/* Modal Content - Stop propagation to prevent closing when clicking inside */}
          <div className="download-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            {!loading && (
              <button onClick={closeModal} className="download-modal-close-btn">
                <X />
              </button>
            )}

            <div className="download-modal-body">
              {/* Animated Header Icons */}
              <div className="header-icons">
                <div className="icon-box indigo animate-float">
                  <FileSpreadsheet />
                </div>
                <div className="icon-box purple animate-float animation-delay-200">
                  <Zap />
                </div>
                <div className="icon-box pink animate-float animation-delay-400">
                  <Clock />
                </div>
              </div>

              {/* Header */}
              <div className="download-modal-header">
                <h2 className="download-modal-title">Download Letter Entries</h2>
              </div>

              {/* Main Status Icon with Glow Effect */}
              <div className="status-icon-container">
                <div className={`status-icon-wrapper ${status === "downloading" ? "animate-pulse" : ""}`}>
                  <div className={`status-icon-glow ${status}`} />
                  <div className={`status-icon-circle ${status === "downloading" ? "downloading" : ""}`}>
                    {getIcon()}
                  </div>
                </div>
              </div>

              {/* Status Message */}
              <div className="status-message">
                <p className="status-text">{getStatusText()}</p>
                {status === "idle" && (
                  <div className="stats-container">
                   
                    <div className="stats-badges">
                      <span className="badge">
                        <span className="badge-dot indigo animate-pulse" />
                        {fileLimit.toLocaleString()} records/file
                      </span>
                     
                    </div>
                  </div>
                )}
              </div>

              {/* Progress Bar with Animated Gradient */}
              {status === "downloading" && (
                <div className="progress-container">
                  <div className="progress-header">
                    <span>Download Progress</span>
                    <span className="progress-percent">{progress}%</span>
                  </div>
                  <div className="progress-bar-bg">
                    <div className="progress-bar-fill" style={{ width: `${progress}%` }}>
                      <div className="progress-bar-shimmer" />
                    </div>
                  </div>
                  <div className="progress-footer">
                    <span>Processing...</span>
                    <span className="progress-count">
                      {stats.fetched.toLocaleString()} / {stats.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons with Animations */}
              <div className="action-buttons">
                {status === "idle" && (
                  <button onClick={handleDownload} className="btn-primary">
                    <span className="btn-primary-shine" />
                    <span className="btn-primary-content">
                      <Download className="btn-primary-icon" />
                      Start Downloading
                    </span>
                  </button>
                )}

                {status === "downloading" && (
                  <button onClick={handleCancel} className="btn-cancel">
                    <XCircle className="btn-cancel-icon" />
                    Cancel Download
                  </button>
                )}

                {(status === "success" || status === "error") && (
                  <div className="action-buttons">
                    <button onClick={handleReset} className="btn-secondary">
                      {status === "success" ? "Export Again" : "Try Again"}
                    </button>
                    {status === "success" && (
                      <button onClick={closeModal} className="btn-text">
                        Close
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Success/Error Details */}
              {status === "success" && (
                <div className="status-panel success">
                  <p className="status-panel-text success">
                    ✨ {stats.total.toLocaleString()} letters exported successfully
                  </p>
                </div>
              )}

              {status === "error" && (
                <div className="status-panel error">
                  <p className="status-panel-text error">
                    Please check your connection and try again
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadAllLetters;